import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { googleSsoLogin } from "../../Action/api.js";
import { decodeJwt } from "../../Utils/index.js";
import { GoogleLogin } from "@react-oauth/google";
import Logo from "../../Assets/images/logo.svg";
import { menuToRoutePath } from "../Users/menuListData.js"; // Import centralized menu config

export const Login = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [googleBtnWidth, setGoogleBtnWidth] = useState(360);
  const navigate = useNavigate();

  /**
   * Decode the JWT authorizationToken returned by the backend and persist
   * all user session data to localStorage, then redirect to the first
   * permitted menu route.
   *
   * Token payload shape (set by backend generateToken):
   *   { userId, menuList, roleName, staffFullName, userStatus, userData, profilePicture }
   */
  const persistUserSession = (responseData) => {
    const token = responseData?.authorizationToken;
    if (!token) {
      setErrorMessage("Unable to establish session. Please try again.");
      return;
    }

    const decoded = decodeJwt(token);
    if (!decoded) {
      setErrorMessage("Unable to process authentication response. Please try again.");
      return;
    }

    // Top-level fields set by backend tokenData
    const userStatus = decoded.userStatus;
    if (userStatus !== "Active") {
      setErrorMessage("Your account is inactive. Please contact your administrator.");
      return;
    }

    const userData     = decoded.userData     || {};
    const userId       = decoded.userId       || userData.userId       || userData.emailId || "";
    const userName     = decoded.staffFullName || userData.name        || userData.staffFullName || "";
    const roleName     = decoded.roleName     || userData.roleName     || "";
    const roleId       = userData.roleId      || "";
    const depId        = userData.depId       || "";
    const depName      = userData.depName     || "";
    const gradeCode    = userData.grade       || userData.gradeCode    || "";
    const menuList     = Array.isArray(decoded.menuList) ? decoded.menuList : (Array.isArray(userData.menuList) ? userData.menuList : []);
    const profilePicture = decoded.profilePicture || userData.profilePicture || "";

    localStorage.setItem("authToken",       token);
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("loginTime",       String(Date.now()));
    localStorage.setItem("userId",          userId);
    localStorage.setItem("userName",        userName);
    localStorage.setItem("staffId",         userData.uId || userId);
    localStorage.setItem("roleId",          roleId);
    localStorage.setItem("roleName",        roleName);
    localStorage.setItem("depId",           depId);
    localStorage.setItem("depName",         depName);
    localStorage.setItem("gradeCode",       gradeCode);
    localStorage.setItem("userStatus",      userStatus);
    localStorage.setItem("userData",        JSON.stringify(userData));
    localStorage.setItem("menuList",        JSON.stringify(menuList));
    if (profilePicture) localStorage.setItem("profilePicture", profilePicture);

    const defaultRoute = menuList.find((menu) => menu.permission === "yes");
    const initialRoute = defaultRoute
      ? (menuToRoutePath[defaultRoute.menuId] || "/dashboard")
      : "/dashboard";
    navigate(initialRoute);
  };

  const handleGoogle = async (credentialResponse) => {
    setErrorMessage("");

    try {
      const googleCredential = credentialResponse?.credential;
      if (!googleCredential) {
        setErrorMessage("Google authentication failed. Missing credential token.");
        return;
      }

      const response = await googleSsoLogin(googleCredential);

      if (!response.data || response.isAxiosError || !response.data.status) {
        setErrorMessage(
          response.data?.uiMsg ||
            "Google login failed. Please contact admin to enable your Google account."
        );
        return;
      }

      persistUserSession(response.data);
    } catch (error) {
      const errorCode = error?.code || "";
      const errorMessageText = error?.message || "";

      console.error("Google sign-in error:", {
        code: errorCode,
        message: errorMessageText,
        raw: error,
      });

      if (errorCode === "auth/network-request-failed") {
        setErrorMessage("Network error during Google sign-in. Please check internet and try again.");
      } else {
        setErrorMessage("Google sign-in failed. Please try again.");
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: { xs: 2, md: 4 },
        overflow: "hidden",
        background:
          "radial-gradient(650px 360px at 12% 10%, rgba(37,99,235,0.16), transparent 60%), radial-gradient(500px 300px at 92% 8%, rgba(14,165,233,0.18), transparent 62%), radial-gradient(560px 280px at 50% 100%, rgba(139,92,246,0.1), transparent 68%), linear-gradient(140deg, #f8fbff 0%, #eef4ff 50%, #f7fafc 100%)",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "980px",
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(37, 99, 235, 0.14)",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Grid container>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: "100%",
                minHeight: { xs: 260, md: 560 },
                p: { xs: 3, md: 5 },
                color: "#FFFFFF",
                position: "relative",
                background:
                  "radial-gradient(450px 220px at 18% 10%, rgba(56,189,248,0.35), transparent 70%), linear-gradient(155deg, #0A1F44 0%, #102A60 40%, #1D4ED8 100%)",
              }}
            >
              <Chip
                label="ORUCORE ENTERPRISE"
                sx={{
                  mb: 2.2,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  color: "#DDEBFF",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
              />
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.85rem", md: "2.6rem" },
                  lineHeight: 1.12,
                  letterSpacing: "-0.02em",
                  maxWidth: 420,
                }}
              >
                Welcome back.
                <br />
                Let us get you in.
              </Typography>
              <Typography sx={{ mt: 2.2, color: "rgba(255,255,255,0.85)", lineHeight: 1.7, maxWidth: 380 }}>
                Use secure Google SSO to access your CRM workspace, insights, and team workflows.
              </Typography>

              <Box sx={{ mt: 3.2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  "Google SSO",
                  "Secure Session",
                  "Role-based Access",
                ].map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    sx={{
                      color: "#FFFFFF",
                      border: "1px solid rgba(255,255,255,0.26)",
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  right: -40,
                  bottom: -40,
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: { xs: 3, md: 5 }, height: "100%", display: "flex", alignItems: "center" }}>
              <Box sx={{ width: "100%" }}>
                <Box
                  onClick={() => navigate("/")}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    mb: 2.2,
                  }}
                >
                  <Box
                    component="img"
                    src={Logo}
                    alt="Orucore"
                    sx={{
                      height: 44,
                      width: "auto",
                      transition: "transform 0.22s ease, filter 0.22s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        filter: "drop-shadow(0 6px 10px rgba(37,99,235,0.15))",
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    textAlign: "center",
                    fontWeight: 900,
                    color: "#0A1F44",
                    letterSpacing: "-0.02em",
                    mb: 0.8,
                  }}
                >
                  Login to Orucore
                </Typography>
                <Typography sx={{ textAlign: "center", color: "#4B5563", mb: 2.2 }}>
                  Continue with your Google account
                </Typography>

                <Divider sx={{ mb: 2.2, borderColor: "rgba(148,163,184,0.25)" }} />

                {errorMessage && (
                  <Alert severity="error" sx={{ marginBottom: 2, textAlign: "left", borderRadius: 2 }}>
                    {errorMessage}
                  </Alert>
                )}

                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 3.5,
                    border: "1px solid #E5E7EB",
                    background:
                      "linear-gradient(160deg, #FFFFFF 0%, rgba(246,250,255,0.92) 100%)",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                    ref={(el) => {
                      if (el) {
                        const width = Math.max(260, Math.min(380, Math.floor(el.clientWidth - 8)));
                        if (width !== googleBtnWidth) {
                          setGoogleBtnWidth(width);
                        }
                      }
                    }}
                  >
                    <GoogleLogin
                      onSuccess={handleGoogle}
                      onError={() => setErrorMessage("Google authentication failed. Please try again.")}
                      theme="outline"
                      size="large"
                      width={String(googleBtnWidth)}
                      text="continue_with"
                      shape="rectangular"
                      logo_alignment="center"
                    />
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  sx={{ color: "#94A3B8", display: "block", mt: 1.5, textAlign: "center" }}
                >
                  By continuing, you agree to your organization access policies.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Login;
